import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useReport } from 'lib/hooks/queries/reports/useReport';

import { getDefaultReportFilters } from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/defaultFilters';
import { generateColumnDefs } from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';
import {
  DEFAULT_CUSTOM_REPORT,
  applyFilters
} from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/helpers';
import { TABS } from 'components/practice/BeyondBilling/Reports/lib/constants';
import { DEFAULT_STALE_TIME } from 'components/shared/AgTablePage/lib/constants';
import { createQueryFilters } from 'components/shared/AgTablePage/lib/createQueryFilters';
import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';

import { ReportContext } from './ReportContext';

export const ReportContextProvider = ({ children }) => {
  const [searchParams] = useSearchParams();

  const category = searchParams.get('category') || TABS[0].value;
  const reportKey = searchParams.get('key');
  const id = searchParams.get('reportId');

  const gridRef = useRef(null);

  const defaultFilters = getDefaultReportFilters(category, reportKey);

  const [initialRender, setInitialRender] = useState(true);
  const [filters, setFilters] = useState(applyFilterHandler(defaultFilters, null));
  const [customReport, setCustomReport] = useState(DEFAULT_CUSTOM_REPORT);

  const queryFilters = useMemo(() => createQueryFilters(filters), [filters]);

  const { data, isLoading } = useReport({
    name,
    params: { id, filters: queryFilters, withCount: true },
    options: {
      staleTime: DEFAULT_STALE_TIME
    },
    dependencies: [id, queryFilters]
  });

  const report = data?.report;

  const populateFilters = useCallback(() => {
    if (!initialRender) return;

    const { filters: reportFilters, custom_ui } = report || {};

    if (reportFilters) {
      setFilters(applyFilters(defaultFilters, reportFilters));

      if (custom_ui) {
        setCustomReport(custom_ui);
      }
    }

    if (report) setInitialRender(false);
  }, [report, initialRender, defaultFilters]);

  useEffect(() => {
    populateFilters();
  }, [populateFilters]);

  const columnDefs = useMemo(() => {
    return generateColumnDefs(report?.result);
  }, [report]);

  return (
    <ReportContext.Provider
      value={{
        report,
        isLoading,
        filters,
        setFilters,
        defaultFilters,
        reportId: report?.report_id || id,
        customReport,
        setCustomReport,
        gridRef,
        columnDefs
      }}>
      {children}
    </ReportContext.Provider>
  );
};
